import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import {
  operations,
  policyholderRules,
  type LimitHistoryEntry,
  type LimitHistoryItem,
  type Policyholder,
  type RuleExecution,
  type RuleExecutionKind,
} from '@/shared/api/resources';
import type { Money } from '@/shared/api/types';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime, formatMoney, formatRate } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../../shared/fieldError';
import {
  eventosDoHistoricoDeLimites,
  gruposDeModalidade,
  origensDaExecucao,
  PENDENCIA_LEITURA_DO_MOTOR,
  PENDENCIA_REPROCESSAMENTO,
  permissaoReprocessamento,
  rotuloDoResultadoDoMotor,
  rotuloDoTipoDeRegra,
  situacoesDaExecucao,
  tiposDeRegra,
} from '../rotulos';
import { Bloco, Secao } from './shared';

// Regras Executadas, Reprocessamento e o historico de Limites e Taxas do tomador
// (P-TOM-C2). O servidor executa, confere a permissao e grava; a tela mostra o que
// foi registrado e nao decide nada.

function simNao(valor: boolean | null): string {
  if (valor === null) return 'nao avaliado';
  return valor ? 'Sim' : 'Nao';
}

function tomDaSituacao(execucao: RuleExecution): 'success' | 'warning' | 'neutral' {
  if (execucao.status !== 'executada') return 'neutral';
  return execucao.success ? 'success' : 'warning';
}

function resultadoDe(execucao: RuleExecution): string {
  if (execucao.engine_run)
    return rotuloDoResultadoDoMotor(execucao.engine_run.final_result);
  return situacoesDaExecucao[execucao.status] ?? execucao.status;
}

// --- Reprocessamento ---------------------------------------------------------------

/**
 * Botao "Reprocessamento" do Cadastro Completo (TOM-030). So aparece para quem tem a
 * permissao, como o botao do ADMIN no OnPoint.
 */
export function BotaoDeReprocessamento({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [tipos, setTipos] = useState<RuleExecutionKind[]>(
    tiposDeRegra.map((tipo) => tipo.value),
  );
  const [operacao, setOperacao] = useState('');

  const operacoes = useQuery({
    queryKey: ['policyholder', tomador.id, 'operations-for-reprocessing'],
    queryFn: ({ signal }) =>
      operations.list({ policyholder_id: tomador.id, limit: 100 }, signal),
    enabled: aberto && can('operacoes.operacao.view'),
  });

  const reprocessar = useMutation({
    mutationFn: () =>
      policyholderRules.reprocess(tomador.id, {
        run_kinds: tipos,
        operation_id: operacao === '' ? null : operacao,
      }),
    onSuccess: (resultado) => {
      const motor = resultado.executions.find((e) => e.run_kind === 'motor_credito');
      toast.success(
        motor && motor.status !== 'executada'
          ? 'Reprocessamento registrado. O motor nao executou: veja o motivo em Regras Executadas.'
          : 'Reprocessamento registrado em Regras Executadas.',
      );
      setAberto(false);
      void queryClient.invalidateQueries({ queryKey: ['policyholder', tomador.id] });
    },
  });
  const erro = reprocessar.error instanceof ApiError ? reprocessar.error : null;

  if (!can(permissaoReprocessamento)) return null;

  function alterna(tipo: RuleExecutionKind, marcado: boolean) {
    setTipos((atuais) =>
      marcado ? [...atuais, tipo] : atuais.filter((atual) => atual !== tipo),
    );
  }

  return (
    <>
      <Button size="sm" variant="secondary" onClick={() => setAberto(true)}>
        Reprocessamento
      </Button>
      <Modal
        open={aberto}
        onClose={() => setAberto(false)}
        title="Reprocessar o tomador"
        description={PENDENCIA_REPROCESSAMENTO}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAberto(false)}>
              Cancelar
            </Button>
            <Button
              loading={reprocessar.isPending}
              disabled={tipos.length === 0}
              onClick={() => reprocessar.mutate()}
            >
              Reprocessar
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-xs font-semibold text-ink-700">
              Regras a reprocessar
            </legend>
            {tiposDeRegra.map((tipo) => (
              <label
                key={tipo.value}
                className="flex items-center gap-2 text-xs font-semibold text-ink-700"
              >
                <input
                  type="checkbox"
                  className="size-4"
                  checked={tipos.includes(tipo.value)}
                  onChange={(event) => alterna(tipo.value, event.target.checked)}
                />
                {tipo.label}
                {tipo.value === 'motor_credito' ? null : (
                  <span className="font-normal text-ink-500">
                    (sem regra aprovada: registra a pendencia)
                  </span>
                )}
              </label>
            ))}
          </fieldset>
          {tipos.includes('motor_credito') ? (
            <Select
              label="Operacao avaliada pelo motor"
              value={operacao}
              onChange={(event) => setOperacao(event.target.value)}
              placeholder="A da ultima execucao do motor"
              options={(operacoes.data?.items ?? []).map((item) => ({
                value: item.id,
                label: `Operacao ${item.number}${item.subject ? `: ${item.subject}` : ''}`,
              }))}
              error={fieldErrorOf(erro, 'operation_id')}
            />
          ) : null}
          <p className="text-2xs text-ink-500">
            O motor e o mesmo da operacao: le a consulta ja feita, nao consulta o
            bureau, e o limite que ele calcula fica ao lado do aprovado, sem muda-lo.
          </p>
          {erro && !fieldErrorOf(erro, 'operation_id') ? (
            <Alert tone="danger" title="Nao foi possivel reprocessar">
              {erro.message}
            </Alert>
          ) : null}
        </div>
      </Modal>
    </>
  );
}

// --- Regras Executadas ---------------------------------------------------------------

export function RegrasExecutadas({ tomador }: { tomador: Policyholder }) {
  const [aberta, setAberta] = useState<RuleExecution | null>(null);
  const execucoes = useQuery({
    queryKey: ['policyholder', tomador.id, 'rule-executions'],
    queryFn: ({ signal }) => policyholderRules.executions(tomador.id, {}, signal),
  });
  const todas = execucoes.data ?? [];
  const ultimas = tiposDeRegra.map((tipo) => ({
    tipo,
    ultima: todas.find((e) => e.run_kind === tipo.value && e.is_last) ?? null,
  }));

  return (
    <Secao
      titulo="Regras Executadas"
      descricao="Execucoes de regras sobre o tomador: as do reprocessamento e as do motor de credito pelas operacoes. So o motor executa; as outras regras registram a pendencia."
      acao={<BotaoDeReprocessamento tomador={tomador} />}
      carregando={execucoes.isPending}
      erro={execucoes.error}
    >
      <DataTable
        columns={[
          { key: 'regra', header: 'Regra', render: (l) => l.tipo.label },
          {
            key: 'executada',
            header: 'Executada em',
            render: (l) =>
              l.ultima ? formatDateTime(l.ultima.executed_at) : 'Nenhuma execucao',
          },
          {
            key: 'situacao',
            header: 'Situacao',
            render: (l) =>
              l.ultima ? (
                <Badge tone={tomDaSituacao(l.ultima)}>{resultadoDe(l.ultima)}</Badge>
              ) : (
                '-'
              ),
          },
          {
            key: 'acoes',
            header: 'Acoes',
            render: (l) =>
              l.ultima ? (
                <AcaoComIcone
                  acao="visualizar"
                  rotulo="Visualizar"
                  onClick={() => setAberta(l.ultima)}
                  aria-label={`Visualizar ${l.tipo.label}`}
                />
              ) : null,
          },
        ]}
        items={ultimas}
        keyOf={(l) => l.tipo.value}
      />
      <Bloco titulo="Todas as execucoes">
        <DataTable
          columns={[
            {
              key: 'data',
              header: 'Executada em',
              render: (e: RuleExecution) => formatDateTime(e.executed_at),
            },
            {
              key: 'regra',
              header: 'Regra',
              render: (e: RuleExecution) => rotuloDoTipoDeRegra(e.run_kind),
            },
            {
              key: 'resultado',
              header: 'Resultado',
              render: (e: RuleExecution) => resultadoDe(e),
            },
            {
              key: 'origem',
              header: 'Origem',
              render: (e: RuleExecution) => origensDaExecucao[e.origin] ?? e.origin,
            },
            {
              key: 'operacao',
              header: 'Operacao',
              render: (e: RuleExecution) =>
                e.operation_id ? (
                  <Link to={`/operacoes/${e.operation_id}`}>Abrir operacao</Link>
                ) : (
                  '-'
                ),
            },
            {
              key: 'simulada',
              header: 'Simulada',
              render: (e: RuleExecution) =>
                e.engine_run?.simulated ? <Badge tone="warning">SIMULADO</Badge> : '-',
            },
            {
              key: 'quem',
              header: 'Solicitado por',
              render: (e: RuleExecution) => e.requested_label || '-',
            },
            {
              key: 'acoes',
              header: 'Acoes',
              render: (e: RuleExecution) => (
                <AcaoComIcone
                  acao="visualizar"
                  rotulo="Visualizar"
                  onClick={() => setAberta(e)}
                />
              ),
            },
          ]}
          items={todas}
          keyOf={(e) => e.id}
          empty={
            <EmptyState
              icon="refresh"
              title="Nenhuma execucao de regra para este tomador"
            />
          }
        />
      </Bloco>
      <DetalheDaExecucao
        tomadorId={tomador.id}
        execucao={aberta}
        onClose={() => setAberta(null)}
      />
    </Secao>
  );
}

function DetalheDaExecucao({
  tomadorId,
  execucao,
  onClose,
}: {
  tomadorId: string;
  execucao: RuleExecution | null;
  onClose: () => void;
}) {
  const detalhe = useQuery({
    queryKey: ['policyholder', tomadorId, 'rule-execution', execucao?.id],
    queryFn: ({ signal }) =>
      policyholderRules.execution(tomadorId, execucao?.id ?? '', signal),
    enabled: execucao !== null,
  });
  const d = detalhe.data;

  return (
    <Modal
      open={execucao !== null}
      onClose={onClose}
      title={execucao ? `Execucao: ${rotuloDoTipoDeRegra(execucao.run_kind)}` : ''}
      size="lg"
    >
      {detalhe.isPending ? <p className="text-sm text-ink-500">Carregando...</p> : null}
      {detalhe.error ? (
        <Alert tone="danger" title="Nao foi possivel abrir a execucao">
          {detalhe.error.message}
        </Alert>
      ) : null}
      {d ? (
        <div className="flex flex-col gap-4">
          <dl className="grid gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-2xs font-semibold uppercase text-ink-500">
                Executada em
              </dt>
              <dd>{formatDateTime(d.executed_at)}</dd>
            </div>
            <div>
              <dt className="text-2xs font-semibold uppercase text-ink-500">
                Situacao
              </dt>
              <dd>{situacoesDaExecucao[d.status] ?? d.status}</dd>
            </div>
            <div>
              <dt className="text-2xs font-semibold uppercase text-ink-500">
                Ultima do tipo
              </dt>
              <dd>{d.is_last ? 'Sim' : 'Nao'}</dd>
            </div>
            <div>
              <dt className="text-2xs font-semibold uppercase text-ink-500">Sucesso</dt>
              <dd>{simNao(d.success)}</dd>
            </div>
            <div>
              <dt className="text-2xs font-semibold uppercase text-ink-500">Fatal</dt>
              <dd>{simNao(d.fatal)}</dd>
            </div>
            <div>
              <dt className="text-2xs font-semibold uppercase text-ink-500">Alerta</dt>
              <dd>{simNao(d.warning)}</dd>
            </div>
            <div>
              <dt className="text-2xs font-semibold uppercase text-ink-500">
                Aprovacao manual
              </dt>
              <dd>{simNao(d.manual_approval_enabled)}</dd>
            </div>
            {d.engine_run ? (
              <>
                <div>
                  <dt className="text-2xs font-semibold uppercase text-ink-500">
                    Modo
                  </dt>
                  <dd>{d.engine_run.mode}</dd>
                </div>
                <div>
                  <dt className="text-2xs font-semibold uppercase text-ink-500">
                    Resultado do motor
                  </dt>
                  <dd>{rotuloDoResultadoDoMotor(d.engine_run.final_result)}</dd>
                </div>
              </>
            ) : null}
          </dl>
          {d.note ? (
            <Alert tone={d.status === 'executada' ? 'info' : 'warning'}>{d.note}</Alert>
          ) : null}
          {d.pending_rules.length > 0 ? (
            <p className="text-xs text-ink-700">
              Regras pendentes: {d.pending_rules.join(', ')}
            </p>
          ) : null}
          {d.engine_run ? (
            <p className="text-2xs text-ink-500">{PENDENCIA_LEITURA_DO_MOTOR}</p>
          ) : null}
          {d.rules.length > 0 ? (
            <DataTable
              columns={[
                {
                  key: 'regra',
                  header: 'Regra',
                  render: (r) => `${r.formula_id} ${r.rule_name}`,
                },
                {
                  key: 'resultado',
                  header: 'Resultado',
                  render: (r) =>
                    `${rotuloDoResultadoDoMotor(r.effective)}${r.shadow ? ' (sombra)' : ''}`,
                },
                {
                  key: 'mensagens',
                  header: 'Mensagens',
                  render: (r) => r.messages.join(' ') || '-',
                },
                { key: 'acao', header: 'Acao', render: (r) => r.action || '-' },
              ]}
              items={d.rules}
              keyOf={(r) => r.formula_id}
            />
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

// --- Historico de Limites e Taxas ----------------------------------------------------

function dinheiro(valor: Money | null): string {
  return valor ? formatMoney(valor) : '-';
}

function taxa(bps: number | null): string {
  return bps === null ? '-' : formatRate(bps);
}

/** Antes e depois de um valor, como o dialogo de historico mostra. */
function antesEDepois(antes: string, depois: string): string {
  return antes === depois ? depois : `${antes} > ${depois}`;
}

/**
 * Sub-aba "Historico" de Limites e Taxas: Data, Evento, Usuario e Acoes, com
 * "Visualizar alteracao automatica" no evento do motor, como no OnPoint.
 */
export function HistoricoDeLimites({ eventos }: { eventos: LimitHistoryEntry[] }) {
  const [aberto, setAberto] = useState<LimitHistoryEntry | null>(null);
  return (
    <>
      <DataTable
        columns={[
          {
            key: 'data',
            header: 'Data',
            render: (e: LimitHistoryEntry) => formatDateTime(e.created_at),
          },
          {
            key: 'evento',
            header: 'Evento',
            render: (e: LimitHistoryEntry) =>
              e.event_label || (eventosDoHistoricoDeLimites[e.event] ?? e.event),
          },
          {
            key: 'usuario',
            header: 'Usuario',
            render: (e: LimitHistoryEntry) => e.actor_label || 'sistema',
          },
          {
            key: 'motivo',
            header: 'Motivo',
            render: (e: LimitHistoryEntry) => e.reason || '-',
          },
          {
            key: 'acoes',
            header: 'Acoes',
            render: (e: LimitHistoryEntry) => (
              <AcaoComIcone
                acao="visualizar"
                rotulo={
                  e.changed_manually
                    ? 'Visualizar alteracao'
                    : 'Visualizar alteracao automatica'
                }
                onClick={() => setAberto(e)}
              />
            ),
          },
        ]}
        items={eventos}
        keyOf={(e) => e.id}
        empty={
          <EmptyState icon="clock" title="Nenhuma alteracao de limite registrada" />
        }
      />
      <Modal
        open={aberto !== null}
        onClose={() => setAberto(null)}
        title={
          aberto?.changed_manually === false
            ? 'Alteracao automatica de limites e taxas'
            : 'Alteracao de limites e taxas'
        }
        description={
          aberto
            ? `${aberto.event_label || (eventosDoHistoricoDeLimites[aberto.event] ?? aberto.event)} em ${formatDateTime(aberto.created_at)}, por ${aberto.actor_label || 'sistema'}.${aberto.reason ? ` Motivo: ${aberto.reason}` : ''}`
            : undefined
        }
        size="lg"
      >
        <DataTable
          columns={[
            {
              key: 'grupo',
              header: 'Grupo de modalidades',
              render: (i: LimitHistoryItem) =>
                gruposDeModalidade[i.modality_group] ?? i.modality_group,
            },
            {
              key: 'aprovado',
              header: 'Novo limite revisado',
              render: (i: LimitHistoryItem) =>
                antesEDepois(dinheiro(i.previous_approved), dinheiro(i.new_approved)),
            },
            {
              key: 'disponivel',
              header: 'Novo limite disponivel',
              render: (i: LimitHistoryItem) => dinheiro(i.new_available),
            },
            {
              key: 'taxa',
              header: 'Taxa Publico/Privado',
              render: (i: LimitHistoryItem) =>
                `${antesEDepois(taxa(i.previous_public_rate_bps), taxa(i.new_public_rate_bps))} / ${antesEDepois(taxa(i.previous_private_rate_bps), taxa(i.new_private_rate_bps))}`,
            },
            {
              key: 'motor',
              header: 'Limite do motor',
              render: (i: LimitHistoryItem) =>
                antesEDepois(
                  dinheiro(i.previous_engine_limit),
                  dinheiro(i.new_engine_limit),
                ),
            },
            {
              key: 'manual',
              header: 'Alterado manualmente',
              render: (i: LimitHistoryItem) => (i.changed_manually ? 'Sim' : 'Nao'),
            },
          ]}
          items={aberto?.items ?? []}
          keyOf={(i) => i.modality_group}
        />
        {aberto?.changed_manually === false ? (
          <p className="mt-3 text-2xs text-ink-500">
            O evento automatico registra o limite que o motor calculou. O aprovado nao
            muda: aplicar o resultado do motor ao limite esta pendente de regra.
          </p>
        ) : null}
      </Modal>
    </>
  );
}
